import { computed, ref } from 'vue'
import type { ChartExplanationData } from '@/types/chartExplanation'

interface ChatTriggerData {
  initialMessage: string
  context?: 'chart-explanation' | 'general'
  chartData?: ChartExplanationData
}

const showChat = ref(false)
const pendingChatData = ref<ChatTriggerData | null>(null)

export function useGlobalChat() {
  const triggerChatWithChart = (chartData: ChartExplanationData) => {
    const formattedMessage = formatChartForChat(chartData)

    pendingChatData.value = {
      initialMessage: formattedMessage,
      context: 'chart-explanation',
      chartData,
    }

    showChat.value = true
  }

  const triggerChat = (message: string, context: 'chart-explanation' | 'general' = 'general') => {
    pendingChatData.value = {
      initialMessage: message,
      context,
    }

    showChat.value = true
  }

  const openChatOnly = () => {
    showChat.value = true
  }

  const closeChat = () => {
    showChat.value = false
    // Clear pending data immediately
    pendingChatData.value = null
  }

  const clearPendingData = () => {
    pendingChatData.value = null
  }

  const consumePendingData = () => {
    const data = pendingChatData.value
    pendingChatData.value = null
    return data
  }

  return {
    showChat: computed(() => showChat.value),
    pendingChatData: computed(() => pendingChatData.value),
    triggerChatWithChart,
    triggerChat,
    openChatOnly,
    closeChat,
    clearPendingData,
    consumePendingData,
  }
}

function formatChartForChat(data: ChartExplanationData): string {
  const { dataPoints, context } = data

  // Build structured data object for the LLM
  const structuredData: any = {
    visualization: {
      title: context.chartTitle || 'Financial Data Visualization',
      description: context.chartDescription,
      timeframe: context.timeframe,
    },
    metrics: {},
    dataPoints: [],
    insights: {
      keyValues: {},
      trends: {},
      milestones: [],
    },
  }

  // Extract wealth projection data (typically line charts)
  if (context.currentAge && context.retirementAge) {
    const currentYear = new Date().getFullYear()
    const currentPoint =
      dataPoints.find((dp) => parseInt(dp.label) === currentYear || dp.index === 0) || dataPoints[0]
    const retirementPoint = dataPoints.find(
      (dp) => dp.tooltip?.year === context.retirementAge?.toString(),
    )
    const finalPoint = dataPoints[dataPoints.length - 1]

    structuredData.metrics = {
      currentAge: context.currentAge,
      retirementAge: context.retirementAge,
      projectionYears: dataPoints.length,
      startYear: dataPoints[0]?.label || currentYear.toString(),
      endYear: finalPoint?.label || (currentYear + dataPoints.length - 1).toString(),
    }

    structuredData.insights.keyValues = {
      currentWealth: currentPoint?.tooltip?.netWealth || 0,
      retirementWealth: retirementPoint?.tooltip?.netWealth || 0,
      finalWealth: finalPoint?.tooltip?.netWealth || 0,
    }

    // Calculate growth rates if we have data
    if (
      currentPoint &&
      finalPoint &&
      currentPoint.tooltip?.netWealth &&
      finalPoint.tooltip?.netWealth
    ) {
      const years = dataPoints.length - 1
      const startWealth = currentPoint.tooltip.netWealth
      const endWealth = finalPoint.tooltip.netWealth
      const totalGrowth = ((endWealth - startWealth) / Math.abs(startWealth)) * 100
      const annualGrowthRate = Math.pow(endWealth / startWealth, 1 / years) - 1

      structuredData.insights.trends = {
        totalGrowthPercent: Math.round(totalGrowth * 100) / 100,
        annualGrowthRate: Math.round(annualGrowthRate * 10000) / 100,
      }
    }

    // Extract detailed data points with all financial components
    structuredData.dataPoints = dataPoints.map((dp) => ({
      year: dp.label,
      age: context.currentAge ? context.currentAge + dp.index : null,
      netWealth: dp.tooltip?.netWealth || 0,
      superannuation: dp.tooltip?.super || 0,
      assets: dp.tooltip?.assets || 0,
      debts: dp.tooltip?.debts || 0,
      milestone: dp.tooltip?.milestone,
      goal: dp.tooltip?.goal,
      ages: dp.tooltip?.ages,
    }))

    // Extract milestones
    structuredData.insights.milestones = dataPoints
      .filter((dp) => dp.tooltip?.milestone)
      .map((dp) => ({
        year: dp.label,
        age: context.currentAge ? context.currentAge + dp.index : null,
        milestone: dp.tooltip?.milestone,
        goal: dp.tooltip?.goal,
        wealthAtMilestone: dp.tooltip?.netWealth || 0,
      }))
  }

  // Extract engagement data (typically doughnut charts)
  else if (dataPoints.some((dp) => dp.tooltip?.percentage !== undefined)) {
    const engagementPoint = dataPoints.find((dp) => dp.tooltip?.percentage !== undefined)

    structuredData.metrics = {
      engagementType: 'memberAdvice',
      totalMembers: engagementPoint?.tooltip?.activeMembers || 0,
      adviceIssued: engagementPoint?.tooltip?.adviceIssued || 0,
      moduleLabel: engagementPoint?.tooltip?.moduleLabel,
    }

    structuredData.insights.keyValues = {
      engagementRate: engagementPoint?.tooltip?.percentage || 0,
      adviceReceived: engagementPoint?.tooltip?.adviceIssued || 0,
      membersWithoutAdvice:
        (engagementPoint?.tooltip?.activeMembers || 0) -
        (engagementPoint?.tooltip?.adviceIssued || 0),
    }

    structuredData.dataPoints = dataPoints.map((dp) => ({
      label: dp.label,
      value: Object.values(dp.values)[0] || 0,
      percentage: dp.tooltip?.percentage || 0,
    }))
  }

  // Extract comparative data (typically bar charts)
  else {
    structuredData.metrics = {
      comparisonType: 'categorical',
      categories: dataPoints.length,
      dataType: 'comparative',
    }

    structuredData.dataPoints = dataPoints.map((dp) => ({
      category: dp.label,
      values: dp.values,
      primaryValue: Object.values(dp.values)[0] || 0,
    }))

    // Calculate comparative insights
    const values = dataPoints.map((dp) => (Object.values(dp.values)[0] as number) || 0)
    const maxValue = Math.max(...values)
    const minValue = Math.min(...values)
    const avgValue = values.reduce((sum, val) => sum + val, 0) / values.length

    structuredData.insights.keyValues = {
      highest: {
        category: dataPoints.find((dp) => Object.values(dp.values)[0] === maxValue)?.label,
        value: maxValue,
      },
      lowest: {
        category: dataPoints.find((dp) => Object.values(dp.values)[0] === minValue)?.label,
        value: minValue,
      },
      average: Math.round(avgValue * 100) / 100,
    }
  }

  return `Please analyse this financial data and provide insights:

\`\`\`json
${JSON.stringify(structuredData, null, 2)}
\`\`\``
}

function formatCurrency(amount: number): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`
  } else {
    return `$${amount.toLocaleString()}`
  }
}
